// Pure draft <-> value coercion for the Settings catalog editors.
//
// `initialDraft` seeds an editor's local state from a setting's stored value;
// `normalize` turns the editor's draft back into the value we PUT to the API.
// Kept here (not in the page component) so the type coercion is testable and
// has a single definition.

import type { SettingPublic } from "./api.js";

type SettingType = SettingPublic["type"];

/** Seed an editor's draft state from the setting's current value. */
export function initialDraft(setting: SettingPublic): unknown {
  if (setting.type === "boolean") return Boolean(setting.value);
  if (setting.type === "account_ids") return Array.isArray(setting.value) ? setting.value : [];
  return setting.value ?? "";
}

/**
 * Turn an editor draft into the value sent to the API.
 * - `account_ids` → always an array.
 * - empty / null / undefined → `null` (clears the override).
 * - `boolean` / `number` → coerced primitive.
 * - everything else → string (objects fall back to JSON).
 */
export function normalize(type: SettingType, draft: unknown): unknown {
  if (type === "account_ids") return Array.isArray(draft) ? draft : [];
  if (draft === "" || draft === null || draft === undefined) return null;
  if (type === "boolean") return Boolean(draft);
  if (type === "number") return Number(draft);
  if (typeof draft === "string" || typeof draft === "number" || typeof draft === "boolean") return String(draft);
  return JSON.stringify(draft);
}
